import { useEffect, useMemo, useState } from "react";

// ─── Standalone, unauthenticated room board for a wall-mounted TV ─────────────
// Loaded by AppShell when the URL is  ?display=rooms  (optionally &date=YYYY-MM-DD).
// Polls the sanitized calendar board endpoint; private documents are never read.

const dateStr = d => new Intl.DateTimeFormat("en-CA", { timeZone:"Europe/London",year:"numeric",month:"2-digit",day:"2-digit" }).format(d);
const timeLabel = (s, e) => (s ? (e ? `${s}\u2013${e}` : s) : "");
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
function parseDate(str) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str || "");
  if (!m) return null;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12));
}

export default function PublicRoomDisplay() {
  const params = new URLSearchParams(window.location.search);
  const urlDate = params.get("date");
  const [today, setToday] = useState(urlDate || dateStr(new Date()));
  const [rooms, setRooms] = useState([]);
  const [entries, setEntries] = useState([]);
  const [clock, setClock] = useState(new Date());
  const [dim, setDim] = useState({ w: window.innerWidth, h: window.innerHeight });
  const [err, setErr] = useState(false);

  // Roll the board over to the new day at midnight (only when following "today").
  useEffect(() => {
    if (urlDate) return;
    const t = setInterval(() => {
      const now = dateStr(new Date());
      setToday((prev) => (prev !== now ? now : prev));
    }, 30000);
    return () => clearInterval(t);
  }, [urlDate]);

  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onR = () => setDim({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, []);

  useEffect(() => {
    if (!today) return;
    let active = true;
    const controller = new AbortController();
    const refresh = async () => {
      try {
        const response = await fetch(`/calendar/board?date=${encodeURIComponent(today)}`, { signal:controller.signal });
        if (!response.ok) throw new Error("Room display unavailable");
        const data = await response.json();
        if (active) { setRooms(data.rooms || []); setEntries(data.entries || []); setErr(false); }
      } catch (failure) { if (active) setErr(true); }
    };
    setRooms([]); setEntries([]);
    refresh();
    const timer = setInterval(refresh, 20000);
    return () => { active = false; controller.abort(); clearInterval(timer); };
  }, [today]);

  const visibleRooms = useMemo(
    () => rooms.filter((r) => r && r.active !== false).sort((a, b) => (a.order || 0) - (b.order || 0)),
    [rooms]
  );

  const itemsByRoom = useMemo(() => {
    const m = {};
    entries.forEach((e) => { if (!e) return; (m[e.roomId] = m[e.roomId] || []).push(e); });
    Object.values(m).forEach((list) => list.sort((a, b) => String(a.startTime || "").localeCompare(String(b.startTime || ""))));
    return m;
  }, [entries]);

  // Choose a column/row count that tiles all rooms across a landscape screen with
  // no scrolling, favouring gently landscape-shaped cells.
  const n = Math.max(visibleRooms.length, 1);
  const headerH = Math.max(dim.h * 0.14, 90);
  const bodyH = Math.max(dim.h - headerH, 1);
  let cols = 1;
  {
    let best = Infinity;
    for (let c = 1; c <= n; c++) {
      const r = Math.ceil(n / c);
      const cellAspect = (dim.w / c) / (bodyH / r);
      const score = Math.abs(Math.log(cellAspect / 1.6));
      if (score < best) { best = score; cols = c; }
    }
  }
  const rows = Math.ceil(n / cols);
  const dObj = parseDate(today) || new Date();

  return (
    <div style={{ position: "fixed", inset: 0, background: "#0f0d0b", color: "#F5EDE0", fontFamily: "'DM Sans',system-ui,sans-serif", overflow: "hidden", display: "flex", flexDirection: "column" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=DM+Sans:wght@400;600;700;800&display=swap'); html,body,#root{margin:0;height:100%;background:#0f0d0b;}`}</style>

      <div style={{ height: headerH, flex: "0 0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 3vw", borderBottom: "2px solid #2a241d", background: "linear-gradient(180deg,#1a1613,#0f0d0b)" }}>
        <div style={{ fontFamily: "'Playfair Display',serif", fontSize: "clamp(22px,4.4vh,64px)", fontWeight: 700, letterSpacing: "1px" }}>Room Allocations</div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: "clamp(16px,3.4vh,42px)", fontWeight: 800, color: "#C4A962" }}>{DAYS[dObj.getUTCDay()]} {dObj.getUTCDate()} {MONTHS[dObj.getUTCMonth()]}</div>
          <div style={{ fontSize: "clamp(13px,2.2vh,26px)", color: "#A09484", fontWeight: 600 }}>{new Intl.DateTimeFormat("en-GB", { timeZone:"Europe/London",hour:"2-digit",minute:"2-digit" }).format(clock)}</div>
        </div>
      </div>

      <div style={{ flex: "1 1 auto", display: "grid", gridTemplateColumns: `repeat(${cols},1fr)`, gridTemplateRows: `repeat(${rows},1fr)`, gap: "1.1vh", padding: "1.4vh 1.4vw", minHeight: 0 }}>
        {visibleRooms.length === 0 && (
          <div style={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", justifyContent: "center", color: "#8B7E6A", fontSize: "3vh" }}>
            {err ? "Waiting for room data\u2026" : "No rooms configured yet"}
          </div>
        )}
        {visibleRooms.map((room) => {
          const items = itemsByRoom[room.id] || [];
          return (
            <div key={room.id} style={{ background: "#17130f", border: "1px solid #2a241d", borderRadius: "1.2vh", display: "flex", flexDirection: "column", overflow: "hidden", minHeight: 0 }}>
              <div style={{ padding: "0.9vh 1.1vw", background: "#231c15", color: "#C4A962", fontFamily: "'Playfair Display',serif", fontWeight: 700, fontSize: "clamp(15px,2.7vh,34px)", letterSpacing: "0.5px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flex: "0 0 auto" }}>
                {String(room.label || room.name || "Room").toUpperCase()}
              </div>
              <div style={{ flex: "1 1 auto", display: "flex", flexDirection: "column", justifyContent: items.length ? "flex-start" : "center", gap: "0.7vh", padding: "0.9vh 1.1vw", minHeight: 0, overflow: "hidden" }}>
                {items.length ? items.map((it) => {
                  const label = (it.bookingSnapshot && it.bookingSnapshot.lodgeName) || it.freeText || "\u2014";
                  const tl = timeLabel(it.startTime, it.endTime);
                  return (
                    <div key={it.id} style={{ lineHeight: 1.05 }}>
                      <div style={{ fontSize: "clamp(16px,3vh,40px)", fontWeight: 800, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</div>
                      {tl && <div style={{ fontSize: "clamp(12px,1.9vh,22px)", fontWeight: 600, color: "#C4A962" }}>{tl}</div>}
                    </div>
                  );
                }) : <div style={{ fontSize: "clamp(14px,2.4vh,30px)", color: "#5f574a", fontWeight: 600 }}>Available</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
